import {
  MediaType,
  PickerMode,
  messageFormatMarkers,
  useShareLocation,
} from '@gripp/shared-logic';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Animated,
  Easing,
  FlatList,
  Keyboard,
  LayoutAnimation,
  Platform,
  Pressable,
  Text,
  UIManager,
  View,
} from 'react-native';
import { AddButton } from '../crop-circle/AddButton';
import {
  AiSquareIcon,
  ArrowUpIcon,
  BoldIcon,
  BulletListIcon,
  CameraIcon,
  DocumentIcon,
  ImageIcon,
  ItalicIcon,
  LinkIcon,
  LocationIcon,
  OrderedListIcon,
  StrikethroughIcon,
  TextIcon,
  UnderlineIcon,
} from '../crop-circle/icons';
import { colors } from '../crop-circle/tokens/colors';
import { RenderMediaItem } from '../media/renderMediaItem';
import { useTypedMedia } from '../media/useTypedMedia';
import { MentionInput } from '../mentionInputField';
import { useMentionTextFormatting } from '../mentionInputField/useMentionTextFormatting';
import type { IssueFooterProps } from './issueFooter';
import { Colors } from '../../themes/colors';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

// LayoutAnimation drives the pill <-> card morph on Android (no-op on the new
// architecture, where it is enabled by default).
if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

/** Animates the next layout pass: used for the pill <-> card morph and for
 *  media previews entering / leaving the card. */
const animateLayout = () =>
  LayoutAnimation.configureNext(
    LayoutAnimation.create(
      220,
      LayoutAnimation.Types.easeInEaseOut,
      LayoutAnimation.Properties.opacity
    )
  );

/**
 * Mobile chat composer (Figma V6 "Text Navigation", nodes 9739:21670 idle /
 * 9739:22162 active). Two states:
 *
 *  - Idle: a 56pt pill with the orange add button inside and a placeholder.
 *    Tapping the pill expands the composer; tapping the add button opens the
 *    camera / gallery / document quick menu (unchanged behavior).
 *  - Active: a radius-4 card with the mention input on top and a toolbar row
 *    below: leading "T" text-styles toggle, attachment icons, and a 32pt
 *    circular send button. The T toggle crossfades the attachment icons with
 *    the text-styles row in place (same pattern as the Gripp OS
 *    MessageComposer).
 *
 * Every toolbar control is wired: gallery / document / camera attach media,
 * the pin drops a maps link for the device's current position, and the
 * text-style buttons wrap the selection in the markers that
 * `messageFormatting.ts` renders back out in the message list. Dictation is
 * deliberately absent — the iOS and Android keyboards already provide it.
 *
 * The AI icon is contextual: it appears only when the host passes
 * `onSummarize`, so surfaces with nothing to summarise show no dead control.
 */
export const IssueFooterMobile = (props: IssueFooterProps) => {
  const { t } = useTranslation();
  const {
    handleAdd,
    handleUploadProgress,
    typedMedia: messageMedia,
    handleDelete,
    reset: resetMessageMedia,
  } = useTypedMedia([]);

  const [message, setMessage] = useState('');
  const [canSend, setCanSend] = useState(false);
  const [isUploading, setUploading] = useState(false);
  // The mention field reports multiline state; the card layout does not use it.
  const [, setIsMultiline] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [toolbarMode, setToolbarMode] = useState<'attach' | 'text'>('attach');
  const [inputHeight, setInputHeight] = useState<number>(minInputHeight);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  // Same footprint as the main bottom navigation bar (NavMenu / BottomBar):
  // 56pt idle height, 16pt sides, 22pt above the screen edge on every device.
  // While the keyboard is up the bar rides on top of it, so only a small gap
  // remains between the card and the keyboard.
  const bottomPadding = keyboardVisible ? keyboardGap : bottomBarOffset;

  useEffect(() => {
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const show = Keyboard.addListener(showEvent, () =>
      setKeyboardVisible(true)
    );
    const hide = Keyboard.addListener(hideEvent, () =>
      setKeyboardVisible(false)
    );
    return () => {
      show.remove();
      hide.remove();
    };
  }, []);

  const cameraOpenRef = useRef<(() => void) | null>(null);
  const galleryOpenRef = useRef<(() => void) | null>(null);
  const isPickingDocumentRef = useRef(false);

  // Set on touch-down of any toolbar control. A tap on a toolbar button can
  // blur the input (the keyboard drops for pickers, and platform quirks can
  // blur before the press lands); without this guard the empty composer
  // would collapse mid-tap and swallow the action (e.g. the T toggle).
  const toolbarInteractionRef = useRef(false);
  const toolbarInteractionTimeout = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const markToolbarInteraction = () => {
    toolbarInteractionRef.current = true;
    if (toolbarInteractionTimeout.current) {
      clearTimeout(toolbarInteractionTimeout.current);
    }
    toolbarInteractionTimeout.current = setTimeout(() => {
      toolbarInteractionRef.current = false;
    }, 500);
  };

  useEffect(
    () => () => {
      if (toolbarInteractionTimeout.current) {
        clearTimeout(toolbarInteractionTimeout.current);
      }
    },
    []
  );

  // 0 = attachment icons visible, 1 = text-style row visible.
  const toolbarAnim = useRef(new Animated.Value(0)).current;

  const { shareLocation } = useShareLocation();
  const isSharingLocationRef = useRef(false);

  const {
    selection,
    onSelectionChange,
    wrapSelection,
    insertLink,
    prefixLines,
  } = useMentionTextFormatting({ value: message, onChange: setMessage });

  const MediaPicker = props.mediaPicker;

  const showPreviewGallery = messageMedia.length > 0;
  const hasVideoInPreview = messageMedia.some(
    (m) => m.type === MediaType.Video
  );
  const previewAreaHeight = hasVideoInPreview
    ? mediaContainerHeightWithVideo
    : mediaContainerHeight;

  useEffect(() => {
    setCanSend((messageMedia.length > 0 && !isUploading) || message.length > 0);
  }, [messageMedia, message, isUploading]);

  const expand = () => {
    animateLayout();
    setExpanded(true);
  };

  const collapse = () => {
    animateLayout();
    setExpanded(false);
    setToolbarMode('attach');
    toolbarAnim.setValue(0);
  };

  // Media picked from the idle-state quick menu (or returning from the
  // camera after the keyboard blur collapsed the card) must surface the
  // preview, which only renders in the expanded card.
  useEffect(() => {
    if (messageMedia.length > 0) {
      animateLayout();
      setExpanded(true);
    }
  }, [messageMedia.length]);

  const onInputBlur = () => {
    if (toolbarInteractionRef.current) {
      return;
    }
    if (message.length === 0 && messageMedia.length === 0) {
      collapse();
    }
  };

  const toggleToolbarMode = () => {
    const next = toolbarMode === 'attach' ? 'text' : 'attach';
    setToolbarMode(next);
    Animated.timing(toolbarAnim, {
      toValue: next === 'text' ? 1 : 0,
      duration: 250,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start();
  };

  const send = () => {
    Keyboard.dismiss();
    props.onNewMessage({
      message: message,
      media: messageMedia,
    });
    setMessage('');
    resetMessageMedia();
    setIsMultiline(false);
    setInputHeight(minInputHeight);
    collapse();
  };

  const closeMenu = () => setMenuOpen(false);

  const toggleMenu = () => {
    if (menuOpen) {
      closeMenu();
      return;
    }
    Keyboard.dismiss();
    setMenuOpen(true);
  };

  const onTapCamera = () => {
    closeMenu();
    cameraOpenRef.current?.();
  };

  const onTapGallery = () => {
    closeMenu();
    galleryOpenRef.current?.();
  };

  const onTapFile = async () => {
    closeMenu();
    if (isPickingDocumentRef.current || !props.pickDocument) return;
    isPickingDocumentRef.current = true;
    try {
      const mediaItem = await props.pickDocument({
        setUploading,
        onProgress: handleUploadProgress,
      });
      if (mediaItem) handleAdd(mediaItem);
    } finally {
      isPickingDocumentRef.current = false;
    }
  };

  /**
   * Appends a maps link for the current position to the draft rather than
   * sending it straight away, so the sender can caption it ("meet me here")
   * before hitting send. The link renders as a tappable URL in the thread via
   * the existing Hyperlink path.
   */
  const onTapLocation = async () => {
    // The permission prompt and the GPS fix both take a moment; without this
    // guard an impatient double-tap queues a second lookup and appends the
    // link twice.
    if (isSharingLocationRef.current) return;
    isSharingLocationRef.current = true;

    try {
      const result = await shareLocation();

      if (result.status === 'denied') {
        Alert.alert(
          t('issue.location.deniedTitle'),
          t('issue.location.deniedMessage')
        );
        return;
      }

      if (result.status === 'unavailable') {
        Alert.alert(
          t('issue.location.unavailableTitle'),
          t('issue.location.unavailableMessage')
        );
        return;
      }

      setMessage((current) =>
        current.length > 0 && !current.endsWith(' ')
          ? `${current} ${result.url}`
          : `${current}${result.url}`
      );
      // The permission prompt blurs the input, and the blur guard is a short
      // window while a GPS fix is not: re-open the card so the link the user
      // just asked for is actually visible.
      if (!expanded) expand();
    } catch (error) {
      Alert.alert(
        t('issue.location.unavailableTitle'),
        t('issue.location.unavailableMessage')
      );
    } finally {
      isSharingLocationRef.current = false;
    }
  };

  const attachOpacity = toolbarAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0],
  });
  const attachTranslate = toolbarAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -8],
  });
  const textStylesOpacity = toolbarAnim;
  const textStylesTranslate = toolbarAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [8, 0],
  });

  const placeholder = props.placeholder ?? t('common.message');

  // Order matches the Figma text-styles row. Each button edits the draft in
  // place (wrapping the selection, or dropping markers at the caret when there
  // is no selection) and the message list renders the markers back out.
  const textStyleButtons: {
    label: string;
    icon: ReactElement;
    onPress: () => void;
  }[] = [
    {
      label: 'Bold',
      icon: <BoldIcon size={20} color={toolbarIconColor} />,
      onPress: () =>
        wrapSelection(
          messageFormatMarkers.bold.open,
          messageFormatMarkers.bold.close
        ),
    },
    {
      label: 'Italic',
      icon: <ItalicIcon size={20} color={toolbarIconColor} />,
      onPress: () =>
        wrapSelection(
          messageFormatMarkers.italic.open,
          messageFormatMarkers.italic.close
        ),
    },
    {
      label: 'Underline',
      icon: <UnderlineIcon size={20} color={toolbarIconColor} />,
      onPress: () =>
        wrapSelection(
          messageFormatMarkers.underline.open,
          messageFormatMarkers.underline.close
        ),
    },
    {
      label: 'Strikethrough',
      icon: <StrikethroughIcon size={20} color={toolbarIconColor} />,
      onPress: () =>
        wrapSelection(
          messageFormatMarkers.strikethrough.open,
          messageFormatMarkers.strikethrough.close
        ),
    },
    {
      label: 'Link',
      icon: <LinkIcon size={18} color={toolbarIconColor} />,
      onPress: insertLink,
    },
    {
      label: 'Ordered list',
      icon: <OrderedListIcon size={20} color={toolbarIconColor} />,
      onPress: () => prefixLines((index) => `${index + 1}. `),
    },
    {
      label: 'Bulleted list',
      icon: <BulletListIcon size={20} color={toolbarIconColor} />,
      onPress: () => prefixLines(() => '- '),
    },
  ];

  return (
    <View
      style={[
        styles.footer,
        { paddingBottom: bottomPadding },
        props.containerStyle,
      ]}
    >
      {/* Headless pickers stay mounted in both states so the opener refs the
          quick menu and the attachment icons call are always live. */}
      <View style={styles.headlessPicker} pointerEvents="none">
        <MediaPicker
          iconColor={colors.brand.white}
          cardStyle={'contained'}
          mode={PickerMode.CAMERA}
          style={styles.headlessPickerIcon}
          icon="plus"
          iconSize={30}
          setMedia={handleAdd}
          setProgress={handleUploadProgress}
          setUploading={setUploading}
          workspaceId={props.workspaceId}
          dismissKeyboard={Keyboard.dismiss}
          acceptedMediaTypes={props.acceptedMediaTypes}
          onPickerReady={(opener) => {
            cameraOpenRef.current = opener;
          }}
        />
        <MediaPicker
          iconColor={colors.brand.white}
          cardStyle={'contained'}
          mode={PickerMode.PICKER}
          style={styles.headlessPickerIcon}
          icon="plus"
          iconSize={30}
          setMedia={handleAdd}
          setProgress={handleUploadProgress}
          setUploading={setUploading}
          workspaceId={props.workspaceId}
          dismissKeyboard={Keyboard.dismiss}
          acceptedMediaTypes={props.acceptedMediaTypes}
          onPickerReady={(opener) => {
            galleryOpenRef.current = opener;
          }}
        />
      </View>

      {expanded ? (
        <View style={styles.card}>
          {showPreviewGallery && (
            <View
              style={[styles.mediaContainer, { height: previewAreaHeight }]}
            >
              <FlatList
                horizontal={true}
                data={messageMedia}
                style={styles.mediaList}
                renderItem={({ item }) => (
                  <View style={styles.mediaItemContainer} key={item.id}>
                    <RenderMediaItem
                      mediaItem={item}
                      key={item.id}
                      deleteMediaItem={handleDelete}
                      hasLightbox
                      canDelete
                      displayWidth={
                        item.type === MediaType.Video ? 200 : undefined
                      }
                    />
                  </View>
                )}
              />
            </View>
          )}

          <View style={[styles.inputArea, { height: inputHeight }]}>
            <MentionInput
              inputFieldText={message}
              setInputFieldText={setMessage}
              setIsMultiline={setIsMultiline}
              inputHeight={inputHeight}
              setInputHeight={(h) =>
                setInputHeight(Math.max(h, minInputHeight))
              }
              send={send}
              placeholder={placeholder}
              autoFocus
              onBlur={onInputBlur}
              inputTextStyle={styles.cardInputText}
              suggestionsRightOverflow={0}
              onSelectionChange={onSelectionChange}
              selection={selection}
              issueId={props.issueId}
            />
          </View>

          <View style={styles.toolbarRow}>
            <Pressable
              onPress={toggleToolbarMode}
              onPressIn={markToolbarInteraction}
              accessibilityRole="button"
              accessibilityLabel={
                toolbarMode === 'text' ? 'Close text styles' : 'Text styles'
              }
              style={styles.toolbarButton}
            >
              <TextIcon
                size={16}
                color={
                  toolbarMode === 'text'
                    ? colors.neutral[800]
                    : toolbarIconColor
                }
              />
            </Pressable>

            {/* Crossfade zone: attachment icons in flow, text styles overlaid. */}
            <View style={styles.crossfadeZone}>
              <Animated.View
                style={[
                  styles.attachRow,
                  {
                    opacity: attachOpacity,
                    transform: [{ translateX: attachTranslate }],
                  },
                ]}
                pointerEvents={toolbarMode === 'attach' ? 'auto' : 'none'}
              >
                <Pressable
                  onPress={onTapGallery}
                  onPressIn={markToolbarInteraction}
                  accessibilityRole="button"
                  accessibilityLabel="Open gallery"
                  style={styles.toolbarButton}
                >
                  <ImageIcon size={16} color={toolbarIconColor} />
                </Pressable>
                <Pressable
                  onPress={onTapFile}
                  onPressIn={markToolbarInteraction}
                  accessibilityRole="button"
                  accessibilityLabel="Attach file"
                  style={styles.toolbarButton}
                >
                  <DocumentIcon size={16} color={toolbarIconColor} />
                </Pressable>
                <Pressable
                  onPress={onTapCamera}
                  onPressIn={markToolbarInteraction}
                  accessibilityRole="button"
                  accessibilityLabel="Open camera"
                  style={styles.toolbarButton}
                >
                  <CameraIcon size={16} color={toolbarIconColor} />
                </Pressable>
                <Pressable
                  onPress={onTapLocation}
                  onPressIn={markToolbarInteraction}
                  accessibilityRole="button"
                  accessibilityLabel="Share location"
                  style={styles.toolbarButton}
                >
                  <LocationIcon size={16} color={toolbarIconColor} />
                </Pressable>
                {/* Contextual: only rendered where a summary is available. */}
                {props.onSummarize && (
                  <Pressable
                    onPress={props.onSummarize}
                    onPressIn={markToolbarInteraction}
                    accessibilityRole="button"
                    accessibilityLabel="Summarize conversation"
                    style={styles.toolbarButton}
                  >
                    <AiSquareIcon size={16} color={toolbarIconColor} />
                  </Pressable>
                )}
              </Animated.View>

              <Animated.View
                style={[
                  styles.textStylesRow,
                  {
                    opacity: textStylesOpacity,
                    transform: [{ translateX: textStylesTranslate }],
                  },
                ]}
                pointerEvents={toolbarMode === 'text' ? 'auto' : 'none'}
              >
                {textStyleButtons.map((button) => (
                  <Pressable
                    key={button.label}
                    onPress={button.onPress}
                    onPressIn={markToolbarInteraction}
                    accessibilityRole="button"
                    accessibilityLabel={button.label}
                    style={styles.textStyleButton}
                  >
                    {button.icon}
                  </Pressable>
                ))}
              </Animated.View>
            </View>

            <Pressable
              onPress={send}
              onPressIn={markToolbarInteraction}
              disabled={!canSend}
              accessibilityRole="button"
              accessibilityLabel="Send message"
              style={[
                styles.sendButton,
                {
                  backgroundColor: canSend
                    ? Colors.role.surfaceInverse
                    : Colors.role.fillDisabled,
                },
              ]}
            >
              <ArrowUpIcon size={14} color={Colors.role.textOnInverse} />
            </Pressable>
          </View>
        </View>
      ) : (
        <Pressable
          style={styles.idlePill}
          onPress={expand}
          accessibilityRole="button"
          accessibilityLabel={placeholder}
        >
          <AddButton
            size={idleAddButtonSize}
            onPress={toggleMenu}
            active={menuOpen}
            accessibilityLabel={
              menuOpen ? 'Close media picker' : 'Open media picker'
            }
          />
          <Text style={styles.placeholderText} numberOfLines={1}>
            {placeholder}
          </Text>
        </Pressable>
      )}

      {menuOpen && (
        <>
          <Pressable
            style={styles.menuBackdrop}
            onPress={closeMenu}
            accessibilityRole="button"
            accessibilityLabel="Close media picker"
          />
          <View style={styles.menuPill}>
            <Pressable
              style={styles.menuItem}
              onPress={onTapCamera}
              accessibilityRole="button"
              accessibilityLabel="Open camera"
            >
              <CameraIcon size={24} color={Colors.role.textOnInverse} />
            </Pressable>
            <Pressable
              style={styles.menuItem}
              onPress={onTapGallery}
              accessibilityRole="button"
              accessibilityLabel="Open gallery"
            >
              <ImageIcon size={24} color={Colors.role.textOnInverse} />
            </Pressable>
            <Pressable
              style={styles.menuItem}
              onPress={onTapFile}
              accessibilityRole="button"
              accessibilityLabel="Attach file"
            >
              <DocumentIcon size={24} color={Colors.role.textOnInverse} />
            </Pressable>
          </View>
        </>
      )}
    </View>
  );
};

// Single text line (18) plus the slack line the mention field's height
// formula adds: the natural single-line input height inside the card.
const minInputHeight = 36;
/** Side padding, matching the main bottom navigation bar. */
const edgePadding = 16;
/** Idle pill height, matching the main bottom navigation bar. */
const idlePillHeight = 56;
/** The orange add button sitting inside the idle pill. */
const idleAddButtonSize = 32;
/**
 * The pill's 99 radius makes each end a semicircle, so its left cap's centre
 * sits exactly `idlePillHeight / 2` from the edge. Insetting the button by half
 * the leftover height puts its centre on that same point — concentric with the
 * cap — and makes the gap above, below and left of the button identical. The
 * placeholder is then offset by the same amount so all four gaps read equal.
 */
const idleAddButtonInset = (idlePillHeight - idleAddButtonSize) / 2;
/**
 * Optical correction for the gap between the button and the placeholder. A
 * glyph does not fill its advance width: the placeholder's first letter starts
 * ~1.33pt inside its own box (left side bearing), so a literal 12pt box gap
 * reads as ~13.3pt of white space and the text looks pushed away from the
 * button. Subtracting the bearing puts the visible INK 12pt from the circle,
 * matching the other three gaps. Measured at 4 device px on a 3x screen at the
 * placeholder's 14pt size.
 */
const placeholderSideBearing = 4 / 3;
/** Distance from the screen's bottom edge, matching the main bottom
 *  navigation bar's built-in `bottom: 22`. */
const bottomBarOffset = 22;
/** Gap kept between the card and the keyboard while typing. */
const keyboardGap = 8;
const mediaContainerHeight = 120;
/** Fits 200px-wide video preview + controls (200*9/16 + 64 ≈ 176) with margin */
const mediaContainerHeightWithVideo = 200;
const toolbarIconColor = colors.neutral[500];

const styles = themedStyleSheet((role) => ({
  footer: {
    backgroundColor: role.canvas,
    paddingHorizontal: edgePadding,
    paddingTop: 8,
    // paddingBottom is applied inline (safe-area / keyboard aware).
  },
  idlePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: idleAddButtonInset - placeholderSideBearing,
    height: idlePillHeight,
    borderRadius: 99,
    paddingLeft: idleAddButtonInset,
    paddingRight: edgePadding,
    backgroundColor: role.surfaceHover,
  },
  placeholderText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 18,
    color: role.textDisabled,
  },
  card: {
    borderRadius: 4,
    backgroundColor: role.surfaceHover,
    padding: 16,
    minHeight: 124,
    justifyContent: 'space-between',
    gap: 12,
  },
  inputArea: {
    width: '100%',
  },
  // Zero padding: the card's own 16pt padding is the only inset, so the text
  // starts at the same distance from the top as from the sides.
  cardInputText: {
    fontSize: 14,
    lineHeight: 18,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    color: role.textStrong,
  },
  toolbarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    height: 32,
  },
  toolbarButton: {
    width: 16,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  crossfadeZone: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  attachRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  // Spans the full zone between the T toggle and the send button;
  // space-between spreads the equally-sized items edge to edge.
  textStylesRow: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // The attachment icons are Figma exports whose glyphs fill the viewBox
  // (16pt container = 16pt of ink). The text-style icons draw inside ~2/3 of
  // a 24 viewBox, so a 20pt render puts their visible glyph at the same size.
  textStyleButton: {
    width: 20,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButton: {
    width: 32,
    height: 32,
    borderRadius: 99,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  mediaList: {
    width: '100%',
    height: '100%',
    flex: 1,
  },
  mediaItemContainer: {
    alignSelf: 'center',
    marginRight: 8,
  },
  headlessPicker: {
    position: 'absolute',
    top: -9999,
    left: -9999,
    width: 0,
    height: 0,
    opacity: 0,
  },
  headlessPickerIcon: {
    height: 56,
    maxHeight: 56,
    width: 56,
    backgroundColor: colors.brand.core,
    borderRadius: 99,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuBackdrop: {
    position: 'absolute',
    top: -2000,
    left: -2000,
    right: -2000,
    bottom: -2000,
    zIndex: 1,
  },
  menuPill: {
    position: 'absolute',
    bottom: 72,
    left: 24,
    width: 48,
    paddingVertical: 12,
    backgroundColor: role.surfaceInverse,
    borderRadius: 24,
    alignItems: 'center',
    gap: 8,
    zIndex: 2,
  },
  menuItem: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
